import { useLayoutEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { boardMasonry } from "../../shared/notes";

/** One slip's place on the cork: its key (a note's id, "speedrun", "add"), whether it is the open one, and the slip. */
export interface NoticeCell {
  key: string;
  open: boolean;
  node: ComponentChildren;
}

/**
 * The notice board's slips in columns, not rows (docs/notes.md "How notes show"; owner, 2026-10-20, on
 * a short slip beside a tall photo note: "Layout here looks kinda bad"). In the board's order each slip
 * goes under the shorter column, ties to the left (shared/notes.ts boardMasonry), by the heights the
 * page measures; an open slip spans the board where it falls. Until every slip is measured (and where
 * there is no ResizeObserver) they sit in the old rows, so nothing jumps about before the first
 * measure; once measured they move only when a height, the slips or the column count changes. The
 * columns come from `--slip-cols` (2 on a phone, 3 and 4 on a wide window). Each cell keeps its place
 * in the page, so the tab order and a screen reader's order are the board's.
 */
export function NoticeSlips({ cells }: { cells: NoticeCell[] }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const els = useRef(new Map<string, HTMLDivElement>());
  const refs = useRef(new Map<string, (el: HTMLDivElement | null) => void>());
  const observer = useRef<ResizeObserver | null>(null);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [cols, setCols] = useState(0);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === "undefined") return;
    const readCols = () => {
      const n = parseInt(getComputedStyle(box).getPropertyValue("--slip-cols"), 10);
      setCols(n > 0 ? n : 2);
    };
    const measure = () =>
      setHeights((prev) => {
        const next: Record<string, number> = {};
        let changed = Object.keys(prev).length !== els.current.size;
        for (const [key, el] of els.current) {
          next[key] = el.offsetHeight;
          if (prev[key] !== next[key]) changed = true;
        }
        return changed ? next : prev;
      });
    const ro = new ResizeObserver((entries) => {
      if (entries.some((e) => e.target === box)) readCols();
      measure();
    });
    observer.current = ro;
    ro.observe(box);
    for (const el of els.current.values()) ro.observe(el);
    readCols();
    measure();
    return () => {
      ro.disconnect();
      observer.current = null;
    };
  }, []);

  /** One stable ref per slip, so a re-render never unhooks and rehooks it. */
  function refFor(key: string) {
    let ref = refs.current.get(key);
    if (!ref) {
      ref = (el) => {
        const was = els.current.get(key);
        if (was && was !== el) {
          observer.current?.unobserve(was);
          els.current.delete(key);
        }
        if (el) {
          els.current.set(key, el);
          observer.current?.observe(el);
        } else {
          refs.current.delete(key);
        }
      };
      refs.current.set(key, ref);
    }
    return ref;
  }

  const ready = cols > 0 && cells.every((c) => heights[c.key] !== undefined);
  const layout = ready
    ? boardMasonry(
        cells.map((c) => heights[c.key]!),
        cols,
        cells.findIndex((c) => c.open),
      )
    : null;

  return (
    <div ref={boxRef} class={`notice-slips${layout ? " masonry" : ""}`} style={layout ? { height: `${layout.height}px` } : undefined}>
      {cells.map((c, i) => {
        const p = layout?.places[i];
        const style = p ? (p.full ? { top: `${p.top}px`, left: "0" } : { top: `${p.top}px`, left: `calc(${p.col} * (var(--slip-w) + var(--slip-gap)))` }) : undefined;
        return (
          <div key={c.key} ref={refFor(c.key)} class={`notice-cell${c.open ? " open" : ""}`} style={style}>
            {c.node}
          </div>
        );
      })}
    </div>
  );
}
